import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Shield, ShieldCheck, ShieldOff, Loader2, Key, RefreshCw, Copy, Check, Download, AlertTriangle } from "lucide-react";
import { MFAEnrollment } from "./MFAEnrollment";

export function MFAManagement() {
  const [loading, setLoading] = useState(true);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [disableOtp, setDisableOtp] = useState("");
  const [disabling, setDisabling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Recovery codes state
  const [remainingCodes, setRemainingCodes] = useState<number>(0);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [regenerateOtp, setRegenerateOtp] = useState("");
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);
  const [newCodes, setNewCodes] = useState<string[]>([]);
  const [showNewCodes, setShowNewCodes] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [savedConfirmed, setSavedConfirmed] = useState(false);

  const checkMFAStatus = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      
      if (error) throw error;

      // Check for verified TOTP factor
      const verifiedFactor = data.totp.find(f => f.status === 'verified');
      
      if (verifiedFactor) {
        setMfaEnabled(true);
        setFactorId(verifiedFactor.id);
        // Fetch remaining recovery codes count
        fetchRecoveryCodesCount();
      } else {
        // Clean up any stale unverified factors so enrollment works cleanly
        for (const factor of data.totp) {
          if ((factor.status as string) === 'unverified') {
            await supabase.auth.mfa.unenroll({ factorId: factor.id });
          }
        }
        setMfaEnabled(false);
        setFactorId(null);
      }
    } catch (err: any) {
      console.error('Error checking MFA status:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecoveryCodesCount = async () => {
    setLoadingCodes(true);
    try {
      const { data, error } = await supabase.functions.invoke('mfa-recovery-codes', {
        body: { action: 'count' }
      });
      
      if (error) throw error;
      setRemainingCodes(data.remaining || 0);
    } catch (err) {
      console.error('Error fetching recovery codes count:', err);
    } finally {
      setLoadingCodes(false);
    }
  };

  useEffect(() => {
    checkMFAStatus();
  }, []);

  const handleDisableMFA = async () => {
    if (!factorId || disableOtp.length !== 6) return;

    setDisabling(true);
    setError(null);

    try {
      // First verify the code to prove they have access
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: disableOtp,
      });

      if (verifyError) throw verifyError;

      // Delete recovery codes
      await supabase.from('mfa_recovery_codes').delete().neq('id', '00000000-0000-0000-0000-000000000000');

      // Now unenroll
      const { error: unenrollError } = await supabase.auth.mfa.unenroll({
        factorId,
      });

      if (unenrollError) throw unenrollError;

      toast({
        title: "MFA Disabled",
        description: "Two-factor authentication has been disabled for your account.",
      });

      setShowDisableDialog(false);
      setDisableOtp("");
      checkMFAStatus();
    } catch (err: any) {
      console.error('Error disabling MFA:', err);
      setError(err.message || 'Failed to disable MFA. Please check your code and try again.');
      setDisableOtp("");
    } finally {
      setDisabling(false);
    }
  };

  const handleRegenerateCodes = async () => {
    if (!factorId || regenerateOtp.length !== 6) return;

    setRegenerating(true);
    setRegenerateError(null);

    try {
      // First verify the code to prove they have access
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: regenerateOtp,
      });

      if (verifyError) throw verifyError;

      // Generate new recovery codes
      const { data: codesData, error: codesError } = await supabase.functions.invoke('mfa-recovery-codes', {
        body: { action: 'generate' }
      });

      if (codesError) throw codesError;

      setNewCodes(codesData.codes);
      setShowRegenerateDialog(false);
      setRegenerateOtp("");
      setShowNewCodes(true);
      setSavedConfirmed(false);
      
    } catch (err: any) {
      console.error('Error regenerating codes:', err);
      setRegenerateError(err.message || 'Failed to regenerate codes. Please check your code and try again.');
      setRegenerateOtp("");
    } finally {
      setRegenerating(false);
    }
  };

  const copyAllCodes = async () => {
    const codesText = newCodes.join('\n');
    await navigator.clipboard.writeText(codesText);
    setCopiedCodes(true);
    setTimeout(() => setCopiedCodes(false), 2000);
    toast({
      title: "Copied",
      description: "Recovery codes copied to clipboard",
    });
  };

  const downloadCodes = () => {
    const codesText = `PTA Hub MFA Recovery Codes\n${'='.repeat(30)}\n\nSave these codes in a safe place.\nEach code can only be used once.\n\n${newCodes.join('\n')}\n\nGenerated: ${new Date().toLocaleDateString()}`;
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pta-hub-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({
      title: "Downloaded",
      description: "Recovery codes saved to file",
    });
  };

  const finishRegeneration = () => {
    setShowNewCodes(false);
    setNewCodes([]);
    setSavedConfirmed(false);
    fetchRecoveryCodesCount();
    toast({
      title: "Recovery Codes Updated",
      description: "Your new recovery codes are now active.",
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // If MFA is not enabled, show enrollment
  if (!mfaEnabled) {
    return <MFAEnrollment onEnrollmentComplete={checkMFAStatus} />;
  }

  // Show new recovery codes after regeneration
  if (showNewCodes && newCodes.length > 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5 text-amber-500" />
            New Recovery Codes
          </CardTitle>
          <CardDescription>
            Your old recovery codes have been invalidated. Save these new codes now.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 dark:text-amber-200">
              <strong>Important:</strong> Save these codes now. You won't be able to see them again!
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-2 gap-2 p-4 bg-muted/50 rounded-lg">
            {newCodes.map((code, index) => (
              <code 
                key={index} 
                className="px-3 py-2 bg-background rounded text-sm font-mono text-center border"
              >
                {code}
              </code>
            ))}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={copyAllCodes} className="flex-1">
              {copiedCodes ? (
                <>
                  <Check className="mr-2 h-4 w-4 text-green-500" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-4 w-4" />
                  Copy All
                </>
              )}
            </Button>
            <Button variant="outline" onClick={downloadCodes} className="flex-1">
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
          </div>

          <div className="flex items-center space-x-2 pt-4 border-t">
            <Checkbox 
              id="confirm-saved" 
              checked={savedConfirmed}
              onCheckedChange={(checked) => setSavedConfirmed(checked === true)}
            />
            <label
              htmlFor="confirm-saved"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              I have saved my recovery codes in a safe place
            </label>
          </div>

          <Button 
            onClick={finishRegeneration}
            disabled={!savedConfirmed}
            className="w-full"
          >
            Continue
          </Button>
        </CardContent>
      </Card>
    );
  }

  // MFA is enabled - show management
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-green-500" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Your account is protected with two-factor authentication.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-2">
            <Badge variant="default" className="bg-green-500/10 text-green-600 border-green-500/20">
              <ShieldCheck className="mr-1 h-3 w-3" />
              Enabled
            </Badge>
          </div>

          <p className="text-sm text-muted-foreground">
            You'll be asked to enter a verification code from your authenticator app when signing in.
          </p>

          {/* Recovery codes section */}
          <div className="p-4 border rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Key className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Recovery Codes</span>
              </div>
              {loadingCodes ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <Badge variant={remainingCodes <= 3 ? "destructive" : "secondary"}>
                  {remainingCodes} of 10 remaining
                </Badge>
              )}
            </div>
            
            {remainingCodes <= 3 && remainingCodes > 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                You're running low on recovery codes. Consider regenerating them.
              </p>
            )}
            
            {remainingCodes === 0 && (
              <p className="text-xs text-destructive">
                You have no recovery codes left. Regenerate them to maintain account access if you lose your device.
              </p>
            )}

            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowRegenerateDialog(true)}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Regenerate Recovery Codes
            </Button>
          </div>

          <Button 
            variant="destructive" 
            onClick={() => setShowDisableDialog(true)}
          >
            <ShieldOff className="mr-2 h-4 w-4" />
            Disable Two-Factor Authentication
          </Button>
        </CardContent>
      </Card>

      {/* Disable MFA Dialog */}
      <AlertDialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Disable Two-Factor Authentication
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the extra layer of security from your account. Enter your current verification code to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="py-4 space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={disableOtp}
                onChange={setDisableOtp}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setDisableOtp("");
              setError(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisableMFA}
              disabled={disableOtp.length !== 6 || disabling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {disabling ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Disabling...
                </>
              ) : (
                "Disable MFA"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Regenerate Codes Dialog */}
      <AlertDialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Regenerate Recovery Codes
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will invalidate all your existing recovery codes and generate new ones. Enter your current verification code to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="py-4 space-y-4">
            {regenerateError && (
              <Alert variant="destructive">
                <AlertDescription>{regenerateError}</AlertDescription>
              </Alert>
            )}

            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={regenerateOtp}
                onChange={setRegenerateOtp}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setRegenerateOtp("");
              setRegenerateError(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRegenerateCodes}
              disabled={regenerateOtp.length !== 6 || regenerating}
            >
              {regenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                "Regenerate Codes"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
